import { create } from 'zustand';
import { authClient } from '../api/checkClient';
import { getUserCapabilities } from '../utils/accountCapabilities';
import { getStoredToken, saveStoredToken, clearStoredToken } from '../utils/authToken';

// ─── Types ───────────────────────────────────────────────────────────────────

export type UserRole = 'person' | 'enterprise' | 'investor' | 'buyer' | 'admin' | 'operator' | 'dev';

export interface UserCapabilities {
  external_customer: boolean;
  can_create_plant: boolean;
  can_browse_marketplace: boolean;
  can_procure_plant: boolean;
  can_batch_procure: boolean;
  use_external_menu: boolean;
  // Operator boundary
  can_directly_mutate_plant?: boolean;
  can_build_on_behalf_of_customer?: boolean;
  can_claim_plant_ownership?: boolean;
  // Admin/dev boundary
  can_arbitrate?: boolean;
  can_approve_plant_deletion?: boolean;
  can_approve_asset_transfer?: boolean;
  can_manage_accounts?: boolean;
}

export interface User {
  id: string;
  email: string;
  phone: string;
  role: UserRole;
  display_role?: string;
  credits_remaining: number;
  token_usage: number;
  llm_tier: 'local' | 'paid';
  plan_key?: string | null;
  terms_version?: string;
  terms_required?: boolean;
  terms_accepted_at?: string | null;
  capabilities?: UserCapabilities;
}

export interface ClaimActivationAsset {
  id: string;
  name: string;
  status: string;
}

export interface ClaimActivation {
  activated: boolean;
  assets_count: number;
  assets: ClaimActivationAsset[];
  message?: string;
}

interface AuthPayload {
  token: string;
  user: User;
  redirect?: string | null;
  claim_activation?: ClaimActivation | null;
}

export interface RegisterPendingPayload {
  activation_required: boolean;
  email: string;
  message?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  postAuthRedirect: string | null;
  claimActivation: ClaimActivation | null;

  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (email: string, password: string, role?: string, inviteToken?: string, businessProof?: string) => Promise<RegisterPendingPayload>;
  sendOTP: (target: string) => Promise<void>;
  verifyOTP: (target: string, code: string) => Promise<void>;
  acceptTerms: (version?: string) => Promise<void>;
  clearClaimActivation: () => void;
  logout: () => void;
  fetchMe: () => Promise<void>;
  patchUser: (patch: Partial<User>) => void;
}

// ─── Role-based redirect mapping ─────────────────────────────────────────────

export function getRedirectPath(role: UserRole): string {
  switch (role) {
    case 'investor':
    case 'buyer':
      return '/marketplace';
    case 'enterprise':
    case 'person':
    case 'admin':
    case 'operator':
    case 'dev':
      return '/portfolio';
    default:
      return '/portfolio';
  }
}

export function normalizeAuthRedirect(path?: string | null): string | null {
  if (!path) return null;
  if (path === '/rater') return '/';
  if (path.startsWith('/rater/')) return path.slice('/rater'.length);
  return path;
}

export function canBrowseWithPendingTerms(user?: Pick<User, 'role' | 'capabilities' | 'terms_required'> | null): boolean {
  if (!user?.terms_required) return false;
  const caps = getUserCapabilities(user as User);
  return !!caps.can_browse_marketplace && !caps.can_create_plant;
}

// ─── Token persistence ───────────────────────────────────────────────────────

function loadToken(): string | null {
  return getStoredToken();
}

function saveToken(token: string, rememberMe = true): void {
  saveStoredToken(token, rememberMe);
}

function clearToken(): void {
  clearStoredToken();
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  token: loadToken(),
  isAuthenticated: !!loadToken(),
  loading: false,
  postAuthRedirect: null,
  claimActivation: null,

  login: async (email: string, password: string, rememberMe?: boolean) => {
    set({ loading: true });
    try {
      const res = await authClient.post<AuthPayload>(
        '/auth/login',
        { email, password, remember_me: !!rememberMe },
      );
      const {
        token,
        user,
        redirect,
        claim_activation: claimActivation,
      } = res.data;
      saveToken(token, !!rememberMe);
      set({
        token,
        user,
        isAuthenticated: true,
        loading: false,
        postAuthRedirect: normalizeAuthRedirect(redirect),
        claimActivation: claimActivation ?? null,
      });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  register: async (email: string, password: string, role?: string, inviteToken?: string, businessProof?: string) => {
    set({ loading: true });
    try {
      const body: Record<string, string> = { email, password };
      if (role) body.role = role;
      if (inviteToken) body.invite_token = inviteToken;
      if (businessProof) body.business_proof = businessProof;
      const res = await authClient.post<RegisterPendingPayload>(
        '/auth/register',
        body,
      );
      set({
        loading: false,
        claimActivation: null,
      });
      return res.data;
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  sendOTP: async (target: string) => {
    await authClient.post('/auth/otp/send', { target });
  },

  verifyOTP: async (target: string, code: string) => {
    set({ loading: true });
    try {
      const res = await authClient.post<AuthPayload>(
        '/auth/otp/verify',
        { target, code },
      );
      const {
        token,
        user,
        redirect,
        claim_activation: claimActivation,
      } = res.data;
      saveToken(token, true);
      set({
        token,
        user,
        isAuthenticated: true,
        loading: false,
        postAuthRedirect: normalizeAuthRedirect(redirect),
        claimActivation: claimActivation ?? null,
      });
    } catch (err) {
      set({ loading: false });
      throw err;
    }
  },

  acceptTerms: async (version?: string) => {
    const res = await authClient.post<User>('/auth/terms/accept', version ? { version } : {});
    const user = res.data;
    set((state) => ({ ...state, user }));
  },

  clearClaimActivation: () => {
    set({ claimActivation: null });
  },

  patchUser: (patch) => {
    set((state) => state.user ? { user: { ...state.user, ...patch } } : {});
  },

  logout: () => {
    clearToken();
    set({ user: null, token: null, isAuthenticated: false, postAuthRedirect: null, claimActivation: null });
  },

  fetchMe: async () => {
    const { token } = get();
    if (!token) return;
    set({ loading: true });
    try {
      const res = await authClient.get<User>('/auth/me');
      set({ user: res.data, isAuthenticated: true, loading: false });
    } catch {
      // Token invalid — clear auth state
      clearToken();
      set({ user: null, token: null, isAuthenticated: false, loading: false });
    }
  },
}));
